import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, API_ENDPOINTS, useAuthStore } from '@dukene/shared';
import { Button, Badge } from '../components/ui';
import {
  Desktop,
  CurrencyDollar,
  Receipt,
  CheckCircle,
  ArrowCounterClockwise,
  WifiHigh,
  Gear,
  MagnifyingGlass,
} from '@phosphor-icons/react';
import { RegisterActive } from '../components/pos/RegisterActive';
import { DEFAULT_RECEIPT_CONFIGURATION } from '../lib/receiptConfig';

export function PointOfSalePage() {
  const queryClient = useQueryClient();
  const { activeStoreId, user } = useAuthStore();

  const [activeTab, setActiveTab] = useState<'overview' | 'settings'>('overview');
  const [activeRegisterSession, setActiveRegisterSession] = useState<{
    storeId: string;
    registerName: string;
    sessionId: string;
  } | null>(null);

  const [searchQuery, setSearchQuery] = useState('');

  // Fetch store for persistent settings
  const { data: store, isLoading: _storeLoading } = useQuery<any>({
    queryKey: ['merchant-store', activeStoreId],
    queryFn: async () => {
      if (!activeStoreId) return null;
      return apiClient.get(API_ENDPOINTS.MERCHANT.STORE);
    },
    enabled: !!activeStoreId,
  });

  // Fetch real POS orders
  const { data: ordersData, isLoading: _ordersLoading } = useQuery<any>({
    queryKey: ['merchant-orders', activeStoreId],
    queryFn: async () => {
      if (!activeStoreId) return { items: [] };
      const res = await apiClient.get<any>(API_ENDPOINTS.ORDERS.LIST, {
        params: { storeId: activeStoreId, limit: 100 },
      });
      const list = Array.isArray(res) ? res : res?.items || res?.data || [];
      return { items: list.filter((o: any) => o.source === 'pos' || o.source === 'POS' || !o.source) };
    },
    enabled: !!activeStoreId,
  });

  const rawOrders = ordersData?.items || [];
  const todayStr = new Date().toISOString().split('T')[0];
  const todayOrders = rawOrders.filter((o: any) => (o.createdAt || o.created_at || '').startsWith(todayStr));
  const todayRevenue = todayOrders.reduce((sum: number, o: any) => sum + Number(o.totalAmount || o.total || 0), 0);

  // Settings from store.settings.pos or defaults
  const [offlineMode, setOfflineMode] = useState(true);
  const [autoPrintReceipts, setAutoPrintReceipts] = useState(true);
  const [autoOpenDrawer, setAutoOpenDrawer] = useState(true);
  const [receiptHeader, setReceiptHeader] = useState('Dukene Retail Store');
  const [receiptFooter, setReceiptFooter] = useState('Thank you for shopping with us! Goods once sold are not returnable.');

  useEffect(() => {
    if (store) {
      const posSettings = store.settings?.pos || {};
      const receiptCfg = store.settings?.receiptConfig || {};
      if (posSettings.offlineMode !== undefined) setOfflineMode(posSettings.offlineMode);
      if (posSettings.autoPrint !== undefined) setAutoPrintReceipts(posSettings.autoPrint);
      if (posSettings.autoDrawer !== undefined) setAutoOpenDrawer(posSettings.autoDrawer);
      if (receiptCfg.businessName) setReceiptHeader(receiptCfg.businessName);
      if (receiptCfg.footerNote) setReceiptFooter(receiptCfg.footerNote);
    }
  }, [store]);

  const updateSettingsMutation = useMutation({
    mutationFn: async (updatedSettings: any) => {
      if (!activeStoreId) return;
      const currentSettings = store?.settings || {};
      await apiClient.patch(API_ENDPOINTS.STORES.UPDATE(activeStoreId), {
        settings: {
          ...currentSettings,
          ...updatedSettings,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchant-store', activeStoreId] });
    },
  });

  const handleLaunchRegister = () => {
    const sessionId = `SESSION-${Date.now().toString().slice(-6)}`;
    setActiveRegisterSession({
      storeId: activeStoreId || store?.id || 'store-main',
      registerName: store?.name ? `${store.name} Register` : 'Main Store Register',
      sessionId,
    });
  };

  const handleSaveConfiguration = () => {
    updateSettingsMutation.mutate({
      pos: {
        offlineMode,
        autoPrint: autoPrintReceipts,
        autoDrawer: autoOpenDrawer,
      },
      receiptConfig: {
        ...(store?.settings?.receiptConfig || DEFAULT_RECEIPT_CONFIGURATION),
        businessName: receiptHeader,
        footerNote: receiptFooter,
      },
    });
    alert('POS Settings Saved successfully!');
  };

  const filteredTransactions = rawOrders
    .filter((o: any) => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      const idStr = String(o.id || o.orderNumber || '').toLowerCase();
      const cashierStr = String(o.cashier?.name || o.customer?.name || '').toLowerCase();
      return idStr.includes(q) || cashierStr.includes(q);
    })
    .map((o: any) => ({
      id: o.orderNumber || o.id || `RCP-${Math.floor(1000 + Math.random() * 9000)}`,
      time: new Date(o.createdAt || o.created_at || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      storeName: store?.name || 'Main Store Register',
      cashier: o.cashier?.name || (user as any)?.name || `${(user as any)?.firstName || ''} ${(user as any)?.lastName || ''}`.trim() || 'Cashier #1',
      paymentMethod: o.paymentMethod || o.payments?.[0]?.method || 'Cash',
      status: o.status || 'Completed',
      total: Number(o.totalAmount || o.total || 0),
    }));

  if (activeRegisterSession) {
    return (
      <RegisterActive
        sessionId={activeRegisterSession.sessionId}
        storeId={activeRegisterSession.storeId || activeStoreId || undefined}
        onClose={() => {
          setActiveRegisterSession(null);
          queryClient.invalidateQueries({ queryKey: ['merchant-orders', activeStoreId] });
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* HEADER BANNER */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white dark:bg-[#1A1D21] p-6 rounded-2xl border border-[#D2D5D9] dark:border-slate-800 shadow-2xs">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#202223] text-white dark:bg-emerald-500/20 dark:text-emerald-400 flex items-center justify-center shrink-0">
            <Desktop weight="bold" className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-extrabold text-[#202223] dark:text-white">Point of Sale (POS)</h1>
            </div>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-1">
              High-speed retail registers, multi-location checkout points, and real-time inventory allocation.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['merchant-orders', activeStoreId] })}
            className="h-9 px-3.5 text-xs font-semibold border-[#D2D5D9] dark:border-slate-700"
          >
            <ArrowCounterClockwise className="w-3.5 h-3.5 mr-1.5" />
            Sync Catalog
          </Button>
          <Button
            onClick={() => handleLaunchRegister()}
            className="h-9 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-md"
          >
            Launch POS
          </Button>
        </div>
      </div>

      {/* TABS NAVIGATION */}
      <div className="flex border-b border-[#E1E3E5] dark:border-slate-800 gap-6">
        {[
          { id: 'overview', label: 'Overview & Receipts', icon: Receipt },
          { id: 'settings', label: 'POS Settings', icon: Gear },
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 pb-3 text-xs font-bold transition-colors border-b-2 ${
                isActive
                  ? 'border-[#202223] dark:border-emerald-500 text-[#202223] dark:text-emerald-400'
                  : 'border-transparent text-[#5C5F62] hover:text-[#202223] dark:text-slate-400 dark:hover:text-white'
              }`}
            >
              <Icon weight={isActive ? 'fill' : 'regular'} className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* TAB 1: OVERVIEW & RECEIPTS */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* KPI STATS */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-2xs flex items-center justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] dark:text-slate-400">Today&apos;s POS Revenue</p>
                <p className="text-xl font-extrabold text-[#202223] dark:text-white mt-1">KES {todayRevenue.toLocaleString()}</p>
                <p className="text-[11px] text-emerald-600 font-semibold mt-0.5">Live store receipts</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-emerald-50 dark:bg-emerald-950/50 flex items-center justify-center text-emerald-600">
                <CurrencyDollar weight="fill" className="w-5 h-5" />
              </div>
            </div>

            <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-2xs flex items-center justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] dark:text-slate-400">Catalog Sync Status</p>
                <div className="flex items-center gap-1.5 mt-1">
                  <CheckCircle weight="fill" className="w-4 h-4 text-emerald-500" />
                  <span className="text-base font-extrabold text-[#202223] dark:text-white">Synced</span>
                </div>
                <p className="text-[11px] text-[#5C5F62] mt-0.5">Instant inventory deduction</p>
              </div>
            </div>
          </div>

          {/* RECENT TRANSACTIONS TABLE */}
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl overflow-hidden shadow-2xs">
            <div className="p-3.5 border-b border-[#E1E3E5] dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#F6F6F7]/60 dark:bg-slate-800/50">
              <div>
                <h3 className="text-xs font-bold text-[#202223] dark:text-white">Recent POS Receipts</h3>
                <p className="text-[11px] text-[#5C5F62]">Live stream of in-person retail sales</p>
              </div>

              <div className="relative w-full sm:w-64">
                <MagnifyingGlass className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5C5F62]" />
                <input
                  type="text"
                  placeholder="Search receipt ID or cashier…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-8 pl-8 pr-3 text-xs bg-white dark:bg-slate-900 border border-[#D2D5D9] dark:border-slate-700 rounded-lg focus:outline-none text-[#202223] dark:text-white"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-[#E1E3E5] dark:border-slate-800 bg-[#F6F6F7] dark:bg-slate-800/40 text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] dark:text-slate-400">
                    <th className="py-2.5 px-4">Receipt ID</th>
                    <th className="py-2.5 px-4">Store Register</th>
                    <th className="py-2.5 px-4">Cashier</th>
                    <th className="py-2.5 px-4">Payment Method</th>
                    <th className="py-2.5 px-4">Status</th>
                    <th className="py-2.5 px-4 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E1E3E5] dark:divide-slate-800 text-xs">
                  {filteredTransactions.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-xs text-[#5C5F62]">
                        No matching POS transactions found. Click &quot;Launch POS&quot; to make your first retail sale.
                      </td>
                    </tr>
                  ) : (
                    filteredTransactions.map((tx: any) => (
                      <tr key={tx.id} className="hover:bg-[#F6F6F7]/60 dark:hover:bg-slate-800/30 transition-colors">
                        <td className="py-3 px-4 font-semibold text-[#202223] dark:text-white">
                          <div className="flex items-center gap-2">
                            <Receipt weight="fill" className="w-3.5 h-3.5 text-[#5C5F62]" />
                            <span>{tx.id}</span>
                          </div>
                          <span className="block text-[11px] text-[#5C5F62] mt-0.5">{tx.time}</span>
                        </td>
                        <td className="py-3 px-4 text-[#202223] dark:text-slate-300">{tx.storeName}</td>
                        <td className="py-3 px-4 text-[#202223] dark:text-slate-300">{tx.cashier}</td>
                        <td className="py-3 px-4">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-[#F6F6F7] dark:bg-slate-800 text-[#202223] dark:text-slate-300 border border-[#E1E3E5] dark:border-slate-700">
                            {tx.paymentMethod}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className="text-[10px] font-bold px-2 py-0.5 border-emerald-500/40 text-emerald-600 bg-emerald-500/10">
                            {tx.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right font-bold text-[#202223] dark:text-white">
                          KES {tx.total.toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}



      {/* TAB 4: SETTINGS */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-6 space-y-5 shadow-2xs">
            <h2 className="text-sm font-bold text-[#202223] dark:text-white">General POS Configuration</h2>

            <div className="space-y-4 divide-y divide-[#E1E3E5] dark:divide-slate-800">
              <div className="flex items-center justify-between pt-4 first:pt-0">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <WifiHigh weight="fill" className="w-4 h-4 text-emerald-600" />
                    <p className="text-xs font-bold text-[#202223] dark:text-white">Offline Mode Resilience</p>
                  </div>
                  <p className="text-xs text-[#5C5F62] max-w-lg">
                    Continue accepting cash and encrypted offline transactions during internet outages. Stock deducts and syncs upon reconnection.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setOfflineMode(!offlineMode)}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                    offlineMode ? 'bg-[#202223] dark:bg-white' : 'bg-[#D2D5D9]'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white dark:bg-slate-900 transition ${
                      offlineMode ? 'translate-x-4' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between pt-4">
                <div className="space-y-0.5">
                  <p className="text-xs font-bold text-[#202223] dark:text-white">Automatic Thermal Receipt Printing</p>
                  <p className="text-xs text-[#5C5F62]">
                    Automatically print a physical customer receipt immediately after payment confirmation.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setAutoPrintReceipts(!autoPrintReceipts)}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                    autoPrintReceipts ? 'bg-[#202223] dark:bg-white' : 'bg-[#D2D5D9]'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white dark:bg-slate-900 transition ${
                      autoPrintReceipts ? 'translate-x-4' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between pt-4">
                <div className="space-y-0.5">
                  <p className="text-xs font-bold text-[#202223] dark:text-white">Kick-Out Cash Drawer on Cash Sale</p>
                  <p className="text-xs text-[#5C5F62]">
                    Send automated kick-out pulse to the thermal printer interface when a cash sale is completed.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setAutoOpenDrawer(!autoOpenDrawer)}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                    autoOpenDrawer ? 'bg-[#202223] dark:bg-white' : 'bg-[#D2D5D9]'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white dark:bg-slate-900 transition ${
                      autoOpenDrawer ? 'translate-x-4' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="pt-4 border-t border-[#E1E3E5] dark:border-slate-800 space-y-4 max-w-xl">
              <h3 className="text-xs font-bold text-[#202223] dark:text-white">Thermal Receipt Customization</h3>

              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-[#202223] dark:text-white">
                  Receipt Header Title
                </label>
                <input
                  type="text"
                  value={receiptHeader}
                  onChange={(e) => setReceiptHeader(e.target.value)}
                  className="w-full h-8 px-3 text-xs bg-white dark:bg-slate-900 border border-[#D2D5D9] dark:border-slate-700 rounded-lg text-[#202223] dark:text-white"
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-[#202223] dark:text-white">
                  Receipt Footer Message
                </label>
                <input
                  type="text"
                  value={receiptFooter}
                  onChange={(e) => setReceiptFooter(e.target.value)}
                  className="w-full h-8 px-3 text-xs bg-white dark:bg-slate-900 border border-[#D2D5D9] dark:border-slate-700 rounded-lg text-[#202223] dark:text-white"
                />
              </div>

              <div>
                <Button
                  onClick={handleSaveConfiguration}
                  className="h-8 px-4 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  Save Configuration
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}

export default PointOfSalePage;
